<template>
  <article class="feature-page">
    <div class="max-w-4xl mx-auto px-4 py-10">
      <h2 class="text-2xl lg:text-3xl font-bold text-center mb-4">
        piceditor.io Remove Background: Effortlessly Clear Backgrounds from Your Images
      </h2>
      <p class="text-xl text-center mb-6">Seamlessly Remove Backgrounds with Precision</p>

      <div class="landing-page-feature-descriptions">
        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img1" :second="img1a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>Effortless Background Removal for a Cleaner Look</h4>
            <p>
              Quickly and seamlessly remove backgrounds from your images in just seconds. Ideal for professional
              portraits, e-commerce products, and creative projects, our AI-powered tool ensures clean, precise results
              every time.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img2" :second="img2a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="col-span-12 lg:col-span-5">
            <h4>Remove Backgrounds for Any Scenario</h4>
            <p>
              From product images to personal photos, our tool adapts to every need. Enjoy high-quality,
              professional-grade background removal with minimal effort—perfect for boosting your visual content.
            </p>
          </div>
        </div>

        <div class="landing-page-feature-description">
          <AppComparisonSlider :first="img3" :second="img3a" :stop-at="50" class="col-span-12 lg:col-span-7" />
          <div class="lg:order-first col-span-12 lg:col-span-5">
            <h4>AI-Powered Precision for Stunning Results</h4>
            <p>
              Achieve flawless background removal with our advanced AI technology. Transform your photos into versatile,
              ready-to-use assets with sharp, clean edges and no manual adjustments required.
            </p>
          </div>
        </div>
      </div>

      <p class="mb-4">
        Perfect for creating professional-looking photos, our Remove Background tool strips away the clutter, leaving
        only the subject in focus. Utilize our tool to enhance product images, professional portraits, or any photos
        requiring a clean look. Our AI-driven process ensures high accuracy and edge definition.
      </p>

      <h3 class="text-2xl font-bold mt-12 mb-6">Why Use Remove Background?</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 feature-page__specific">
        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/ai.png" alt="AI technology" />
          <strong>Powered by AI</strong>
          <p>Leverage advanced AI technology for flawless background removal that enhances image focus and quality.</p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/precision.png" alt="precision" />
          <strong>High Precision</strong>
          <p>Accurately detect and erase complex backgrounds, even around challenging edges and fine details.</p>
        </div>

        <div class="flex flex-col space-y-4 items-center text-center">
          <img src="@/images/landing_pages/efficiency.png" alt="efficiency" />
          <strong>Quick and Efficient</strong>
          <p>Streamline your editing workflow with fast processing that returns results within seconds.</p>
        </div>
      </div>

      <h3 class="text-2xl font-bold mt-12 mb-6">How It Works</h3>
      <ol class="list-decimal pl-5 mb-6">
        <li><strong>Upload Your Image:</strong> Drag and drop your image or upload it from your device.</li>
        <li>
          <strong>Remove Background:</strong> Our AI will automatically process your image and remove the background.
        </li>
        <li>
          <strong>Preview & Adjust:</strong> Preview the result and make any necessary adjustments to ensure the perfect
          output.
        </li>
        <li>
          <strong>Download:</strong> Download your image with a transparent or a solid color background as needed.
        </li>
      </ol>

      <p class="">
        Our tool is optimized for both mobile and desktop devices, ensuring you can remove backgrounds from your images
        anywhere, at any time, without the need for complex software.
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
  import AppComparisonSlider from '&/AppComparisonSlider.vue'

  import img1 from '@/images/landing_pages/remove_background/1.png'
  import img1a from '@/images/landing_pages/remove_background/1_after.png'
  import img2 from '@/images/landing_pages/remove_background/2.png'
  import img2a from '@/images/landing_pages/remove_background/2_after.png'
  import img3 from '@/images/landing_pages/remove_background/3.png'
  import img3a from '@/images/landing_pages/remove_background/3_after.png'
</script>
